import React, { useCallback, useRef, useState } from 'react';

import { rootUiActions } from 'app-components/flux/root-ui';
import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { getRootModalRemoveDialogOptions } from 'components/Drawers/standardDrawerContract';
import Dump from 'components/Dump';
import InvestmentTransactionEditForm from 'components/Forms/InvestmentTransactionEditForm/legacy';
import type {
  InvestmentTransactionFormik,
  InvestmentTransactionFormValues,
} from 'components/Forms/InvestmentTransactionEditForm/legacy/types';
import QButton from 'components/QButton';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { useAppDispatch } from 'hooks/redux';
import trackEventTTI from 'utils/trackEventTTI';

import InvestmentTransactionEditSurface from '../components/InvestmentTransactionEditSurface';
import type { InvestmentTransactionEditDialogProps } from '../types';

const InvestmentTransactionEditDialog = React.memo(function InvestmentTransactionEditDialog({
  transaction,
  context,
  dialogId,
  rootModalDrawer,
  ...dialogProps
}: InvestmentTransactionEditDialogProps): React.JSX.Element {
  const dispatch = useAppDispatch();
  const formikRef = useRef<InvestmentTransactionFormik | null>(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const onDirty = useCallback((dirty: boolean) => setDisableSubmit(!dirty), []);
  const { deleteTransactions } = useTxnActions({ wrapperId: context });

  const closeForm = useCallback(
    (_event?: React.SyntheticEvent, _reason?: string) => {
      if (dialogId) {
        dispatch(rootUiActions.removeDialog(dialogId, getRootModalRemoveDialogOptions(rootModalDrawer)));
      }
    },
    [dialogId, dispatch, rootModalDrawer],
  );

  const title = transaction?.id ? 'Transaction detail' : 'Create transaction';

  return (
    <InvestmentTransactionEditSurface
      {...dialogProps}
      dialogId={dialogId}
      onClose={closeForm}
      rootModalDrawer={rootModalDrawer}
      title={title}
    >
      <Dump obj={transaction} />

      <DialogContent sx={rootModalDrawer ? { flex: '1 1 auto', minHeight: 0 } : undefined}>
        <InvestmentTransactionEditForm
          context={context}
          transaction={transaction as Partial<InvestmentTransactionFormValues> | undefined}
          formikRef={formikRef}
          onDirty={onDirty}
          onClose={closeForm}
        />
      </DialogContent>

      <DialogActions>
        {transaction?.id ? (
          <QButton
            id='investment-transaction-delete-button'
            variant='delete'
            onClick={() => {
              const payload = transactionsTypes.mkInvestmentTransaction({
                ...transaction,
                isDeleted: true,
              });
              if (assert(payload?.id, 'transaction missing id', { payload, transaction })) {
                const trackingProps = transactionsSelectors.getTrackingProps(undefined, payload);
                trackEventTTI(tracker.events.txnDelete, trackingProps);

                deleteTransactions([payload]);
                closeForm();
              }
            }}
          >
            Delete
          </QButton>
        ) : null}

        <QButton
          variant='contained'
          disabled={Boolean(disableSubmit)}
          onClick={() => formikRef.current?.submitForm?.()}
          id='investment-transaction-edit-submit-button'
        >
          {transaction?.id ? 'Update' : 'Create'}
        </QButton>
      </DialogActions>
    </InvestmentTransactionEditSurface>
  );
});

InvestmentTransactionEditDialog.displayName = 'InvestmentTransactionEditDialog';

export default InvestmentTransactionEditDialog;
